import { FlexRow } from '@/components/common/Flex';
import { BigHeader } from '@/components/common/Typography';
import { useTrackModalShown } from '@/services/analytics/useTracking';
import { useMutation, useQuery } from '@apollo/client';
import { Spinner } from '@chakra-ui/react';
import { getAtomicExternalAccountCreationConsentMd } from '@common/consents';
import {
    CreateAtomicExternalAccountConsentDocument,
    FinancialEntityDocument,
} from '@common/graphql/generated';
import { useState } from 'react';
import { Button } from '../common/Button';
import { FlexCol } from '../common/Flex';
import Markdown from '../common/Markdown';
import Modal from '../common/modals/Modal';
import ModalManager, { useModal } from '../ModalManager';
import { ACCOUNT_LINKING_MODAL_CONTENT_HEIGHT } from './helpers';

export interface ExternalAccountConsentModalProps {
    financialEntityId: string;
    onComplete: VoidFunction;
    onCancel: VoidFunction;
}

export const showExternalAccountConsentModal = (
    props: ExternalAccountConsentModalProps
) => ModalManager.show(ExternalAccountConsentModal, props);

const ExternalAccountConsentModal =
    ModalManager.create<ExternalAccountConsentModalProps>(
        ({ financialEntityId, onComplete, onCancel }) => {
            useTrackModalShown('External Account Consent');

            const modal = useModal();
            const { data } = useQuery(FinancialEntityDocument, {
                variables: { id: financialEntityId },
            });
            const [createAtomicExternalAccountConsent] = useMutation(
                CreateAtomicExternalAccountConsentDocument
            );
            const [isSubmitting, setIsSubmitting] = useState(false);

            const onClose = () => {
                onCancel();
                modal.remove();
            };

            const fe = data?.financialEntity;

            if (!fe || !fe.name || !fe.mask) {
                return (
                    <Modal
                        size="md"
                        isCentered
                        isOpen={modal.visible}
                        onClose={onClose}
                        renderContent={() => (
                            <FlexRow
                                h={ACCOUNT_LINKING_MODAL_CONTENT_HEIGHT}
                                justifyContent="center"
                                alignItems="center"
                            >
                                <Spinner />
                            </FlexRow>
                        )}
                    />
                );
            }

            const title = 'Verify your account';

            const contentsMd = getAtomicExternalAccountCreationConsentMd({
                feName: fe.name,
                feMask: fe.mask,
            });

            const consentInput = {
                financialEntityId,
                title,
                method: 'BUTTON' as const,
                contents: contentsMd,
                buttonText: 'Agree and continue',
            };

            const onSubmit = async () => {
                setIsSubmitting(true);
                try {
                    await createAtomicExternalAccountConsent({
                        variables: consentInput,
                    });
                    onComplete();
                    modal.remove();
                } finally {
                    setIsSubmitting(false);
                }
            };

            return (
                <Modal
                    size="md"
                    isCentered
                    isOpen={modal.visible}
                    closeOnOverlayClick={false}
                    onClose={onClose}
                    renderContent={() => (
                        <FlexCol h={ACCOUNT_LINKING_MODAL_CONTENT_HEIGHT}>
                            <BigHeader mb={4}>Verify your account</BigHeader>
                            <FlexRow bg="gray.100" p={6}>
                                <Markdown content={consentInput.contents} />
                            </FlexRow>
                        </FlexCol>
                    )}
                    renderFooter={() => (
                        <FlexRow px={12} py={6} justifyContent="space-between">
                            <Button
                                variant="link"
                                colorScheme="gray"
                                onClick={onClose}
                                onClickEvent="Cancel External Account Consent Button Clicked"
                                isDisabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={onSubmit}
                                onClickEvent="Confirm External Account Consent Button Clicked"
                                isLoading={isSubmitting}
                                isDisabled={isSubmitting}
                            >
                                {consentInput.buttonText}
                            </Button>
                        </FlexRow>
                    )}
                />
            );
        }
    );

export default ExternalAccountConsentModal;
